import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import api from "../api/axiosInstance";
import NavigationBar from "../layout/NavigationBar";
import Footer from "../layout/footer";
import LoadingSpinner from "./LoadingSpinner";
import CategoryBarOrder from "./CategoryBarOrder";
import UsedMarketplaceCategories from "./UsedMarketplaceCategories";
import 'datatables.net-dt/css/jquery.dataTables.css';
import $ from 'jquery';
import 'datatables.net';
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useTranslation } from "react-i18next";

function Category() {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [categoryData, setCategoryData] = useState([]);
    // Raw rows (one per subcategory) for the category bar order card, which
    // needs each row's category_order.
    const [categoryRows, setCategoryRows] = useState([]);
    // Read once, so the list request and the order card use the same store.
    const [businessId] = useState(() => localStorage.getItem('business_id'));
    const [loading, setLoading] = useState(true);
    const [deleteCategoryId, setDeleteCategoryId] = useState("");
    const [deleteTitle, setDeleteTitle] = useState("");
    const [dataTable, setDataTable] = useState(null);
    const [pageLength, setPageLength] = useState(10);
    
    // Modal state for viewing subcategories
    const [showSubcategoriesModal, setShowSubcategoriesModal] = useState(false);
    const [selectedCategoryData, setSelectedCategoryData] = useState(null);

    // useEffect(() => {
    //     // Update the page layout on component mount
    //     const pathname = window.location.pathname;
    //     if (pathname === '/list') {
    //         console.log("Adding classes to the body.");
    //         document.body.classList.remove('login-page');
    //         document.body.classList.add('sidebar-mini');
    //     }
    // }, []);

    useEffect(() => {
        // Fetch the data from the API on component mount
        fetchData();
        const savedPageLength = localStorage.getItem('categoryListPageLength');
        if (savedPageLength) {
            setPageLength(parseInt(savedPageLength));
        }
    }, []);

    useEffect(() => {
        if (categoryData.length) {
            if (dataTable) dataTable.destroy();

            const table = $('#category').DataTable({
                data: categoryData,
                columns: [
                    { 
                        data: null,
                        orderable: false,
                        searchable: false,
                        render: function (data, type, row, meta) {
                            return meta.row + meta.settings._iDisplayStart + 1;
                        }
                    }, // S.No - properly calculated with pagination
                    { data: 'category' },
                    { 
                        data: 'subcategories',
                        render: (data) => {
                            if (data.length === 0) return 'No subcategories';
                            return data[0].subcategory;
                        }
                    },
                    {
                        data: 'created_date',
                        render: (data) => new Date(data).toLocaleString()
                    },
                    {
                        data: null,
                        orderable: false,
                        render: (data) => `
                        <div class="text-center">
                            <button class="btn btn-sm btn-info view-subcats-btn mr-2" data-category='${JSON.stringify(data).replace(/'/g, "&apos;")}'>
                                <i class="fa-solid fa-eye"></i> View
                            </button>
                            <a href="/category_edit?id=${data._id}" class="btn btn-sm btn-warning mr-2">
                                <i class="fa-solid fa-pen"></i>
                            </a>
                            <button class="btn btn-sm btn-danger delete-btn" 
                               data-id="${data._id}" 
                               data-name="${data.category}"
                               data-toggle="modal" 
                               data-target="#modaldelete">
                                <i class="fa-solid fa-trash-can"></i>
                            </button>
                        </div>
                    `
                    }
                ],
                pageLength: pageLength,
                lengthMenu: [10, 20, 50, 100],
                order: [[3, 'desc']], // Sort by created date descending
                drawCallback: function() {
                    // Recalculate row numbers after any table redraw (sort, filter, page change)
                    this.api().column(0, {page:'current'}).nodes().each(function(cell, i) {
                        cell.innerHTML = i + 1;
                    });
                }
            });

            setDataTable(table);

            table.on('length.dt', function (e, settings, len) {
                setPageLength(len);
                localStorage.setItem('categoryListPageLength', len.toString());
            });

            // Store current page length when edit is clicked
            $("#category").on("click", ".edit-btn", function () {
                const currentLength = table.page.len();
                localStorage.setItem('categoryListPageLength', currentLength.toString());
            });

            // Handle view subcategories button click
            $('#category').on('click', '.view-subcats-btn', function (e) {
                e.preventDefault();
                const categoryDataStr = $(this).data('category');
                if (categoryDataStr) {
                    const categoryData = typeof categoryDataStr === 'string' ? JSON.parse(categoryDataStr) : categoryDataStr;
                    setSelectedCategoryData(categoryData);
                    setShowSubcategoriesModal(true);
                }
            });

            $('#category').on('click', '.delete-btn', function () {
                const categoryId = $(this).data('id');
                const categoryName = $(this).data('name');
                openDeleteModal(categoryId, categoryName);
            });
        }
    }, [categoryData]);


    const fetchData = () => {
        setLoading(true);
        // Fetching category data from the server
        const business_id = businessId;
        // Without business_id the API would return every store's categories.
        if (!business_id) {
            setCategoryData([]);
            setCategoryRows([]);
            setLoading(false);
            return;
        }
        api
            // limit: the API default is 500 rows; the order card needs every
            // category, or /category/reorder would clear the missing ones.
            .get('/category/list', { params: { business_id, limit: 100000 } })
            .then((res) => {
                if (res.data.status === "success") {
                    setCategoryRows(res.data.result);
                    // Sort a copy: categoryRows holds the original array.
                    // Sort by created_date descending (newest first) before grouping
                    const sortedData = [...res.data.result].sort((a, b) => 
                        new Date(b.created_date) - new Date(a.created_date)
                    );
                    
                    // Group by category
                    const grouped = sortedData.reduce((acc, item) => {
                        if (!acc[item.category]) {
                            acc[item.category] = {
                                category: item.category,
                                subcategories: [],
                                created_date: item.created_date,
                                _id: item._id
                            };
                        }
                        acc[item.category].subcategories.push({
                            subcategory: item.subcategory,
                            _id: item._id,
                            created_date: item.created_date
                        });
                        return acc;
                    }, {});
                    
                    // Convert to array and sort by created_date again
                    const groupedArray = Object.values(grouped).sort((a, b) => 
                        new Date(b.created_date) - new Date(a.created_date)
                    );
                    
                    setCategoryData(groupedArray);
                }
                setLoading(false);
            })
            .catch((err) => {
                console.error("Error fetching category data:", err);
                setLoading(false);
            });
    };

    const deleteCategory = (category_id) => {
        api.post('/category/delete', {
            category_id: category_id,
            category_status: "delete"
        })
            .then((res) => {
                if (res.data.status === "success") {
                    // Update state - remove deleted category
                    setCategoryData(categoryData.filter(category => category._id !== category_id));
                    setCategoryRows(rows => rows.filter(row => row._id !== category_id));
                    toast.success("Category deleted successfully!");

                } else if (res.data.status === "error") {
                    toast.error(res.data.message, {
                        autoClose: 8000, 
                    });
                } else {
                    toast.error("Failed to delete category.");
                }
            })
            .catch((err) => {
                console.error("Error deleting category:", err);

                // Check if error response contains mapped products info
                if (err.response && err.response.data && err.response.data.status === "error") {
                    toast.error(err.response.data.message, {
                        autoClose: 8000,
                    });
                } else {
                    toast.error("Something went wrong. Try again!");
                }

                // Close the modal
                $('#modaldelete').modal('hide');
            });
    };


    const openDeleteModal = (id, title) => {
        // Open the delete confirmation modal
        setDeleteCategoryId(id);
        setDeleteTitle(title);
    };
    if (loading) {
        return (
            <div className="wrapper" id="root">
                <LoadingSpinner message="Loading categories..." />
            </div>
        );

    }

    return (
        <div className="wrapper" id="root">
            <header>
                <NavigationBar />
            </header>

            <div className="content-wrapper">
                <div className="content-header">
                    <div className="container-fluid">
                        <div className="row mb-2">
                            <div className="col-sm-6">
                                <h1 className="m-0 txt_lft">
                                    {t('table.category')}
                                    <a href="/category_add">
                                        <i className="fa-solid fa-square-plus ml-2" title="Add Product" />
                                    </a>
                                </h1>
                            </div>
                        </div>
                    </div>
                </div>



                {/* Table / No products found */}
                <div className="content">
                    <div className="container-fluid">
                        <div className="row">
                            <div className="col-lg-12">
                                <UsedMarketplaceCategories
                                    businessId={businessId}
                                    onAdded={fetchData}
                                />
                                <CategoryBarOrder
                                    rows={categoryRows}
                                    businessId={businessId}
                                />
                            </div>
                        </div>
                        <div className="row justify-content-center">
                            <div className="col-lg-12 text-center">
                                {categoryData.length === 0 ? (
                                    <h3 className="fw-bold" style={{ marginTop: "150px" }}>
                                        {t('categoryTab.noCategoriesFound')}
                                    </h3>

                                ) : (
                                    <div className="table-responsive">
                                        <table
                                            id="category"
                                            className="table table-striped table-bordered txt_lft"
                                            style={{ width: '100%' }}
                                        >
                                            <thead>
                                                <tr>
                                                    <th>{t('table.sno')}</th>
                                                    <th>{t('table.category')}</th>
                                                    <th>{t('table.subcategory')}</th>
                                                    <th>{t('table.addedOn')}</th>
                                                    <th>{t('table.action')}</th>
                                                </tr>
                                            </thead>
                                        </table>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <Footer />

            {/* View Subcategories Modal */}
            {showSubcategoriesModal && selectedCategoryData && (
                <div 
                    className="modal fade show" 
                    style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}
                    onClick={() => setShowSubcategoriesModal(false)}
                >
                    <div 
                        className="modal-dialog modal-dialog-centered modal-lg"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="modal-content">
                            <div className="modal-header" style={{ backgroundColor: '#2E7D4F', color: 'white' }}>
                                <h5 className="modal-title">
                                    <i className="fa-solid fa-list mr-2"></i>
                                    Subcategories for "{selectedCategoryData.category}"
                                </h5>
                                <button 
                                    type="button" 
                                    className="close text-white" 
                                    onClick={() => setShowSubcategoriesModal(false)}
                                >
                                    <span>&times;</span>
                                </button>
                            </div>
                            <div className="modal-body">
                                <div className="alert alert-info">
                                    <i className="fa-solid fa-info-circle mr-2"></i>
                                    Total Subcategories: <strong>{selectedCategoryData.subcategories.length}</strong>
                                </div>
                                
                                <div className="table-responsive">
                                    <table className="table table-striped table-bordered">
                                        <thead className="thead-light">
                                            <tr>
                                                <th style={{ width: '60px' }}>S.No</th>
                                                <th>Subcategory Name</th>
                                                <th style={{ width: '180px' }}>Added On</th>
                                                <th style={{ width: '100px' }}>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {selectedCategoryData.subcategories.map((subcat, index) => (
                                                <tr key={subcat._id}>
                                                    <td className="text-center">{index + 1}</td>
                                                    <td>{subcat.subcategory}</td>
                                                    <td>{new Date(subcat.created_date).toLocaleString()}</td>
                                                    <td className="text-center">
                                                        <a 
                                                            href={`/category_edit?id=${subcat._id}`}
                                                            className="btn btn-sm btn-warning"
                                                            title="Edit Subcategory"
                                                        >
                                                            <i className="fa-solid fa-pen"></i>
                                                        </a>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button 
                                    type="button" 
                                    className="btn btn-secondary" 
                                    onClick={() => setShowSubcategoriesModal(false)}
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            <div className="modal fade" id="modaldelete" tabIndex={-1} aria-labelledby="modaldeleteLabel" aria-hidden="true" data-backdrop="static">
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title" id="modaldeleteLabel">Delete Category</h5>
                            <button type="button" className="close" data-dismiss="modal" aria-label="Close">
                                <i className="fa-solid fa-xmark" />
                            </button>
                        </div>
                        <div className="modal-body">
                            <div className="w-100 d-inline-block text-center">
                                Are you sure you want to delete <br /><span className="fwt_600">{deleteTitle}</span>?
                            </div>
                        </div>
                        <div className="modal-footer justify-content-center">
                            <button
                                type="button"
                                className="btn btn-primary"
                                data-dismiss="modal"
                                aria-label="Close"
                                onClick={() => deleteCategory(deleteCategoryId)}
                            >
                                Yes, delete
                            </button>
                            <button
                                type="button"
                                className="btn btn-secondary"
                                data-dismiss="modal"
                                aria-label="Close"
                            >
                                No, cancel
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default Category;
